// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@conklin/App/Components/Notify/Notify";
import CartLineQuantity, { CartLineQuantityStyles } from "@conklin/Overrides/Widgets/Cart/CartLineQuantity";
import { parseBoolean } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
    showRemove?: boolean;
    qtyDisabled?: boolean;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    productInfoWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    basicProductPriceStyles?: ProductPriceStyles;
    extendedNetPriceValue?: TypographyProps;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    removeConfirmation?: InjectableCss;
    removeConfirmationText?: TypographyProps;
    removeConfirmationConfirm?: ButtonPresentationProps;
    removeConfirmationClose?: ButtonPresentationProps;
}

export const miniCartLineStyles: MiniCartLineStyles = {};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
    showRemove = true,
    qtyDisabled = true,
}) => {
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const history = useHistory();

    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.imageWrapper}>
                    <Link
                        href={cartLine.productUri}
                        onClick={() => {
                            history.push(cartLine.productUri);
                        }}
                    >
                        <LazyImage src={cartLine.smallImagePath} altText={cartLine.altText} {...styles.cartLineImage} />
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    <ProductDescription product={cartLine} />
                    <ProductPartNumbers
                        manufacturerItem={cartLine.manufacturerItem}
                        productNumber={cartLine.erpNumber}
                        extendedStyles={styles.partNumbersStyles}
                        customerProductNumber={cartLine.customerName}
                        {...{ sku: cartLine.properties?.sku || "" }}
                    />
                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                        <CartLineQuantity
                            cart={cart as Cart}
                            disabled={qtyDisabled}
                            editable={!parseBoolean(cart?.properties?.erpSystemIsDown)}
                            extendedStyles={styles.cartLineQuantityStyles}
                            onQtyOrderedChange={(inputQty: number) => {
                                qtyOrderedChangeHandler?.(cartLine, inputQty);
                            }}
                        />
                        {!cart?.cartNotPriced && (
                            <ProductPrice
                                showLabel={false}
                                showSavings={false}
                                showUnitOfMeasure={true}
                                product={cartLine}
                                extendedStyles={styles.basicProductPriceStyles}
                            />
                        )}
                        {showRemove && !showRemoveConfirmation && !parseBoolean(cart?.properties?.erpSystemIsDown) && (
                            <Link
                                onClick={() => {
                                    setShowRemoveConfirmation(true);
                                }}
                                data-test-selector="cartline_removeLine"
                                {...styles.removeCartLineLink}
                            />
                        )}
                        <StyledWrapper
                            {...({ showRemoveConfirmation } as any)}
                            {...styles.removeConfirmation}
                            aria-expanded={showRemoveConfirmation}
                        >
                            <Typography {...styles.removeConfirmationText}>{translate("Are You Sure?")}</Typography>
                            <Button
                                onClick={() => {
                                    removeCartLineClickHandler(cartLine);
                                }}
                                {...styles.removeConfirmationConfirm}
                                variant="secondary"
                            >
                                {translate("Yes, Remove")}
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowRemoveConfirmation(false);
                                }}
                                {...styles.removeConfirmationClose}
                                variant="secondary"
                            >
                                {translate("No")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                    showClose={false}
                                    sizeVariant="small"
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
